import { createInvestorEntityRoutes } from "features/investor-entities/InvestorEntityRoutes";
import { getCapitalActivityTableDataRetrieveLoader } from "gen/endpoints/capital-activity/capital-activity";
import { getCommitmentStatsRetrieveLoader } from "gen/endpoints/commitment-stats/commitment-stats";
import { getFeesAndCarryRetrieveLoader } from "gen/endpoints/fees-and-carry/fees-and-carry";
import { getFundListLoader } from "gen/endpoints/fund-entities/fund-entities";
import { getInvestorRetrieveLoader } from "gen/endpoints/investor/investor";
import { getInvestorsTableDataRetrieveLoader } from "gen/endpoints/investor-table-data/investor-table-data";
import i18n from "i18n/config";
import { Navigate, RouteObject } from "react-router-dom";
import { composeLoaders } from "shared/utils/RoutingUtils";

export const investorRoutes: RouteObject = {
  id: "investors",
  path: "investors",
  children: [
    {
      id: "investorsIndex",
      index: true,
      lazy: async () => {
        const { InvestorListPage } = await import(
          "features/investors/InvestorListPage"
        );
        return { Component: InvestorListPage };
      },
      loader: getInvestorsTableDataRetrieveLoader(),
    },
    {
      id: "investorsInvestor",
      path: ":investorId",
      loader: getInvestorRetrieveLoader(),
      children: [
        {
          id: "investorsInvestorIndex",
          index: true,
          element: <Navigate to="activity" replace={true} />,
          handle: {},
        },
        {
          id: "investorsInvestorView",
          lazy: async () => {
            const { InvestorPage } = await import(
              "features/investors/InvestorPage"
            );
            return { Component: InvestorPage };
          },
          children: [
            {
              id: "investorsInvestorActivity",
              path: "activity",
              lazy: async () => {
                const { CommitmentTabActivity } = await import(
                  "features/commitments/view/tabs/CommitmentTabActivity"
                );
                return { Component: CommitmentTabActivity };
              },
              loader: composeLoaders(
                [
                  getFundListLoader,
                  getCommitmentStatsRetrieveLoader,
                  getFeesAndCarryRetrieveLoader,
                  getCapitalActivityTableDataRetrieveLoader,
                ].map((fn) =>
                  fn(({ investorId }) => ({
                    investor_id: investorId!,
                  })),
                ),
              ),
              handle: {
                title: i18n.t("investor:tabs.activity.title"),
              },
            },
            {
              id: "investorsInvestorProfile",
              path: "profile",
              lazy: async () => {
                const { InvestorTabProfile } = await import(
                  "features/investors/tabs/InvestorTabProfile"
                );
                return { Component: InvestorTabProfile };
              },
              handle: {
                title: i18n.t("investor:tabs.profile.title"),
              },
            },
            {
              id: "investorsInvestorFunds",
              path: "funds",
              lazy: async () => {
                const { InvestorTabFunds } = await import(
                  "features/investors/tabs/InvestorTabFunds"
                );
                return { Component: InvestorTabFunds };
              },
              handle: {
                title: i18n.t("investor:tabs.funds.title"),
              },
            },
            {
              id: "investorsInvestorPerformance",
              path: "performance",
              lazy: async () => {
                const { HypotheticalPerformanceTab } = await import(
                  "features/financial-reporting/HypotheticalPerformanceTab"
                );
                return { Component: HypotheticalPerformanceTab };
              },
              handle: {
                title: i18n.t("investor:tabs.performance.title"),
              },
            },
            {
              id: "investorsInvestorEntitiesList",
              path: "investor-entities",
              lazy: async () => {
                const { InvestorTabEntities } = await import(
                  "features/investors/tabs/InvestorTabEntities"
                );
                return { Component: InvestorTabEntities };
              },
              handle: {
                title: i18n.t("investor:tabs.entities.title"),
              },
            },
          ],
        },
        createInvestorEntityRoutes("investors"),
      ],
    },
  ],
};
